/**
 * ApplicationFlow, a Sankey of where applications actually went.
 *
 * Structure: stages are places you pass through and get a node. Outcomes are
 * where you stop, so they have no node. A band that ends in an outcome fades
 * out just past its last stage, coloured by that outcome, with the counts in
 * the legend below.
 *
 * That keeps the columns to real stages, removes the long diagonals that came
 * from every outcome sitting in the far-right column, and still shows what
 * happened to everyone.
 *
 * Paths are the real recorded ones. If someone moved an application straight
 * from Applied to Interview without recording Screening, the chart shows that
 * jump rather than inventing the missing step.
 *
 * Props:
 *   data — { nodes, links, sufficient_data, total_applications } from /metrics/flow
 */

// Stage nodes. Violet ramp so the funnel reads as one narrowing flow.
const STAGE_COLORS = {
  applied: "#4A2DA8",
  screening: "#5F3FBC",
  interview: "#6B4BC8",
  technical_test: "#7B5FD4",
  final_interview: "#8A6EE0",
  offer_received: "#9575E8",
};

// Outcomes. No node, just the colour a band fades into.
const OUTCOME_COLORS = {
  accepted: "#16A34A",
  rejected: "#DC2626",
  withdrawn: "#6B7280",
  closed: "#64748B",
  on_hold: "#D97706",
  ghosted: "#DC2626",
  in_progress: "#D97706",
};

const NODE_WIDTH = 12;
const NODE_GAP = 22;
const COL_WIDTH = 200;
const LEFT_PAD = 40;
const TOP_PAD = 24;
const HEIGHT = 260;
// How far past its source a terminating band runs before it has faded out.
const FADE_LENGTH = 90;

/** Assign each stage node a column: one past the deepest stage feeding it. */
function assignColumns(stageIds, links) {
  const incoming = {};
  stageIds.forEach((id) => (incoming[id] = []));
  links.forEach((l) => {
    if (incoming[l.target] && stageIds.includes(l.source)) {
      incoming[l.target].push(l.source);
    }
  });

  const col = {};
  const resolve = (id, seen = new Set()) => {
    if (col[id] !== undefined) return col[id];
    // Guard against a cycle. The backend should never emit one, but a bad
    // path would otherwise hang the render rather than draw something wrong.
    if (seen.has(id)) return 0;
    seen.add(id);
    const sources = incoming[id] ?? [];
    col[id] =
      sources.length === 0
        ? 0
        : Math.max(...sources.map((s) => resolve(s, seen))) + 1;
    return col[id];
  };
  stageIds.forEach((id) => resolve(id));
  return col;
}

export default function ApplicationFlow({ data }) {
  if (!data) return null;

  if (!data.sufficient_data) {
    const n = data.total_applications ?? 0;
    return (
      <p className="text-gray-400 text-sm text-center py-8">
        Your flow chart appears once you have tracked 5 applications.
        {n > 0 && ` You have ${n} so far.`}
      </p>
    );
  }

  const { nodes, links } = data;
  if (!nodes.length || !links.length) return null;

  const stages = nodes.filter((n) => n.kind === "stage");
  const outcomes = nodes.filter((n) => n.kind === "outcome");
  const stageIds = stages.map((n) => n.id);
  if (!stages.length) return null;

  // Links that end at another stage keep the funnel flowing. Links that end at
  // an outcome terminate, and are drawn as fading bands instead.
  const stageLinks = links.filter((l) => stageIds.includes(l.target));
  const exitLinks = links.filter((l) => !stageIds.includes(l.target));

  const col = assignColumns(stageIds, links);
  const maxCol = Math.max(...stageIds.map((id) => col[id]));

  // A node's weight is everything flowing through it, including the
  // applications that leave from it. Otherwise a stage where most people stop
  // would render narrower than the traffic it actually saw.
  const weight = {};
  stages.forEach((n) => {
    const inSum = links
      .filter((l) => l.target === n.id)
      .reduce((s, l) => s + l.value, 0);
    const outSum = links
      .filter((l) => l.source === n.id)
      .reduce((s, l) => s + l.value, 0);
    weight[n.id] = Math.max(inSum, outSum);
  });

  const byCol = {};
  stages.forEach((n) => {
    (byCol[col[n.id]] ??= []).push(n);
  });

  const heaviestColumn = Math.max(
    ...Object.values(byCol).map((ns) =>
      ns.reduce((s, n) => s + weight[n.id], 0),
    ),
  );
  const mostNodesInAColumn = Math.max(
    ...Object.values(byCol).map((ns) => ns.length),
  );
  const usableHeight =
    HEIGHT - TOP_PAD * 2 - NODE_GAP * (mostNodesInAColumn - 1);
  const unit = usableHeight / (heaviestColumn || 1);

  // Position stages column by column. Within a column, order by where the
  // inbound traffic comes from rather than by size, so bands run roughly flat
  // instead of crossing to reach a node placed elsewhere.
  const pos = {};
  const cols = Object.keys(byCol)
    .map(Number)
    .sort((a, b) => a - b);
  for (const c of cols) {
    const ordered = [...byCol[c]].sort((a, b) => {
      const src = (n) => {
        const feeds = stageLinks.filter((l) => l.target === n.id);
        if (!feeds.length) return -weight[n.id];
        const ys = feeds.map((l) => pos[l.source]?.y ?? 0);
        return ys.reduce((s, y) => s + y, 0) / ys.length;
      };
      return src(a) - src(b);
    });
    let y = TOP_PAD;
    ordered.forEach((n) => {
      const h = Math.max(weight[n.id] * unit, 2);
      pos[n.id] = { x: LEFT_PAD + c * COL_WIDTH, y, h };
      y += h + NODE_GAP;
    });
  }

  // Bands leave a node stacked in a stable order: continuing flows first so
  // they stay near the spine, then exits.
  const outOffset = {};
  const inOffset = {};

  const continuing = [...stageLinks].sort((a, b) => {
    const ay = (pos[a.source]?.y ?? 0) * 1000 + (pos[a.target]?.y ?? 0);
    const by = (pos[b.source]?.y ?? 0) * 1000 + (pos[b.target]?.y ?? 0);
    return ay - by;
  });

  const paths = continuing
    .map((l) => {
      const s = pos[l.source];
      const t = pos[l.target];
      if (!s || !t) return null;

      const thickness = Math.max(l.value * unit, 1.5);
      const sy = s.y + (outOffset[l.source] ?? 0) + thickness / 2;
      const ty = t.y + (inOffset[l.target] ?? 0) + thickness / 2;
      outOffset[l.source] = (outOffset[l.source] ?? 0) + thickness;
      inOffset[l.target] = (inOffset[l.target] ?? 0) + thickness;

      const x0 = s.x + NODE_WIDTH;
      const x1 = t.x;
      const mid = (x0 + x1) / 2;

      return {
        key: `${l.source}-${l.target}`,
        d: `M ${x0} ${sy} C ${mid} ${sy}, ${mid} ${ty}, ${x1} ${ty}`,
        thickness,
        stroke: STAGE_COLORS[l.source] ?? "#4A2DA8",
        label: `${l.value}`,
      };
    })
    .filter(Boolean);

  // Terminating bands. Each runs a short distance past its stage and fades to
  // nothing, coloured by the outcome it ended in.
  const exits = exitLinks
    .map((l, i) => {
      const s = pos[l.source];
      if (!s) return null;

      const thickness = Math.max(l.value * unit, 1.5);
      const sy = s.y + (outOffset[l.source] ?? 0) + thickness / 2;
      outOffset[l.source] = (outOffset[l.source] ?? 0) + thickness;

      const x0 = s.x + NODE_WIDTH;
      const x1 = x0 + FADE_LENGTH;
      // Drift downward slightly so an exit reads as leaving the funnel rather
      // than continuing along it.
      const ty = sy + thickness * 0.6 + 10;
      const mid = (x0 + x1) / 2;

      return {
        key: `exit-${l.source}-${l.target}-${i}`,
        gradientId: `fade-${l.source}-${l.target}-${i}`,
        d: `M ${x0} ${sy} C ${mid} ${sy}, ${mid} ${ty}, ${x1} ${ty}`,
        thickness,
        color: OUTCOME_COLORS[l.target] ?? "#6B7280",
      };
    })
    .filter(Boolean);

  const width = LEFT_PAD + maxCol * COL_WIDTH + NODE_WIDTH + FADE_LENGTH + 40;

  // Legend totals, so removing the outcome nodes does not remove the numbers.
  const outcomeTotals = outcomes
    .map((o) => ({
      id: o.id,
      label: o.label,
      color: OUTCOME_COLORS[o.id] ?? "#6B7280",
      count: exitLinks
        .filter((l) => l.target === o.id)
        .reduce((s, l) => s + l.value, 0),
    }))
    .filter((o) => o.count > 0)
    .sort((a, b) => b.count - a.count);

  return (
    <div className="flex flex-col gap-4">
      <div className="w-full overflow-x-auto">
        <svg
          width={width}
          height={HEIGHT}
          viewBox={`0 0 ${width} ${HEIGHT}`}
          style={{ maxWidth: "100%" }}
          role="img"
          aria-label="Where your applications went"
        >
          <defs>
            {exits.map((e) => (
              <linearGradient
                key={e.gradientId}
                id={e.gradientId}
                x1="0"
                y1="0"
                x2="1"
                y2="0"
              >
                <stop offset="0%" stopColor={e.color} stopOpacity="0.45" />
                <stop offset="100%" stopColor={e.color} stopOpacity="0" />
              </linearGradient>
            ))}
          </defs>

          {/* Terminating bands first, so continuing flow sits on top */}
          <g>
            {exits.map((e) => (
              <path
                key={e.key}
                d={e.d}
                stroke={`url(#${e.gradientId})`}
                strokeWidth={e.thickness}
                fill="none"
                strokeLinecap="round"
              />
            ))}
          </g>

          {/* Continuing flow */}
          <g>
            {paths.map((p) => (
              <path
                key={p.key}
                d={p.d}
                stroke={p.stroke}
                strokeWidth={p.thickness}
                fill="none"
                opacity="0.35"
              >
                <title>{p.label}</title>
              </path>
            ))}
          </g>

          {/* Stage nodes */}
          <g>
            {stages.map((n) => {
              const p = pos[n.id];
              if (!p) return null;
              return (
                <g key={n.id}>
                  <rect
                    x={p.x}
                    y={p.y}
                    width={NODE_WIDTH}
                    height={p.h}
                    fill={STAGE_COLORS[n.id] ?? "#4A2DA8"}
                    rx="2"
                  />
                  <text
                    // Above the node rather than beside it. Middle columns have
                    // flows on both sides, so a side label lands on a band.
                    x={p.x + NODE_WIDTH / 2}
                    y={p.y - 6}
                    textAnchor="middle"
                    className="fill-gray-600 dark:fill-gray-300"
                    fontSize="12"
                  >
                    {n.label}
                    <tspan className="fill-gray-400" dx="6">
                      {weight[n.id]}
                    </tspan>
                  </text>
                </g>
              );
            })}
          </g>
        </svg>
      </div>

      {/* Outcomes live here rather than as nodes, since they are where an
          application stopped rather than a stage it passed through. */}
      {outcomeTotals.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-1">
          {outcomeTotals.map((o) => (
            <div key={o.id} className="flex items-center gap-2">
              <span
                className="w-3 h-1.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: o.color }}
              />
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {o.label}
              </span>
              <span className="text-xs font-semibold text-gray-900 dark:text-white">
                {o.count}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
